import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter/types';
import { Button } from '@shared/components/ui/button';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import { ShippingRulePreset } from '../../models/ShippingRulePreset';
import { usePresetActions } from '../../hooks/usePresetActions';

interface ApplyPresetModalProps {
  onSuccess?: () => void;
}

export interface ApplyPresetModalRef {
  open: (preset: ShippingRulePreset) => void;
}

// Modal para aplicar un preset a un equipo destino
export const ApplyPresetModal = forwardRef<ApplyPresetModalRef, ApplyPresetModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('shippingRulePresetsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [selectedPreset, setSelectedPreset] = useState<ShippingRulePreset | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  const { applyPreset, isLoading } = usePresetActions(onSuccess);

  // Obtener lista de equipos hijos (clientes) para el selector
  const httpClient = createHttpClient(apiClientWH);
  const clientsAdapter = new ClientsApiAdapter(httpClient);

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-preset-apply'],
    queryFn: () => clientsAdapter.getClientsPaginated({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const teamOptions =
    clientsData?.clients.map((client) => ({
      label: client.name,
      value: String(client.id),
    })) ?? [];

  // Exponer método open para control externo
  useImperativeHandle(ref, () => ({
    open: (preset: ShippingRulePreset) => {
      setSelectedPreset(preset);
      setSelectedTeamId('');
      modalRef.current?.openModal();
    },
  }));

  const handleApply = async () => {
    if (!selectedPreset || !selectedTeamId) return;
    try {
      await applyPreset(selectedPreset.id, Number(selectedTeamId));
      modalRef.current?.closeModal();
    } catch {
      // El error ya es manejado en usePresetActions con toast
    }
  };

  return (
    <ModalCenter ref={modalRef} title={t('applyModal.title')} width="default" data-testid="apply-preset-modal">
      {selectedPreset && (
        <div className="flex flex-col gap-4">
          {/* Descripción del modal */}
          <p className="text-sm text-subtext">{t('applyModal.description', { name: selectedPreset.name })}</p>

          {/* Selector de equipo destino */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-title">{t('applyModal.targetTeam')}</label>
            <Combobox
              options={teamOptions}
              value={selectedTeamId}
              setValue={setSelectedTeamId}
              placeholder={t('applyModal.targetTeamPlaceholder')}
              dataTestId="apply-preset-team-select"
            />
          </div>

          {/* Cuadro de información del preset */}
          <div className="rounded-lg border border-card-border bg-card-bg p-4">
            <div className="flex items-center gap-2 mb-3">
              <FontAwesomeIcon icon={faCircleInfo} className="w-3.5 h-3.5 text-icons" />
              <span className="text-sm font-medium text-title">{t('applyModal.info.title')}</span>
            </div>
            <ul className="flex flex-col gap-1.5 pl-1">
              {/* Cantidad de reglas que se aplicarán */}
              <li className="text-sm text-subtext flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-subtext flex-shrink-0" />
                {t('applyModal.info.rulesApplied', {
                  count: selectedPreset.rules_count,
                })}
              </li>
              {/* Aviso de reemplazo de reglas */}
              <li className="text-sm text-subtext flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-subtext flex-shrink-0" />
                {t('applyModal.info.warning')}
              </li>
              {/* Registro en historial */}
              <li className="text-sm text-subtext flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-subtext flex-shrink-0" />
                {t('applyModal.info.log')}
              </li>
            </ul>
          </div>

          {/* Footer con acciones */}
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="apply-preset-cancel-button"
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              onClick={handleApply}
              disabled={!selectedTeamId || isLoading}
              data-testid="apply-preset-confirm-button"
            >
              {t('buttons.applyPreset')}
            </Button>
          </div>
        </div>
      )}
    </ModalCenter>
  );
});

ApplyPresetModal.displayName = 'ApplyPresetModal';
